import { api } from './client';
import type { SlaBadge } from './mrn';

export type PoStatus = 'Draft' | 'Sent' | 'Acknowledged' | 'Delivered' | 'Cancelled';
export type PayStatus = 'Pending' | 'Partial' | 'Paid';

export interface PoItem {
  id?: number;
  itemId?: number | null;
  itemName: string;
  hsn?: string | null;
  qty: number | string;
  unit: string;
  unitPrice: number | string;
  gstPct: number | string;
  total?: number | string;
  /** Sum of receivedQty across every non-cancelled GRN on this PO item.
   *  Set on the PO list response so the GRN modal can compute what's still
   *  pending; absent (undefined) when the field wasn't included. */
  receivedQty?: number;
  /** Item-master thumbnail, attached on the PO list response (GRN modal). */
  imageUrl?: string | null;
  /** Item-master SKU barcode, attached on the PO list response (GRN modal). */
  barcode?: string | null;
  item?: {
    code: string;
    category: string;
    width: string | null;
    length: string | null;
    sizeUnit: string | null;
    mrp: string;
  } | null;
}

export interface Po {
  id: number;
  poNo: string;
  poDate: string;
  /** Short subject, carried forward MRN → PR → PO → GRN. */
  subject?: string | null;
  vendorId: number;
  vendor: { id: number; name: string; gstin?: string | null };
  rfqId: number | null;
  rfq?: { rfqNo: string; id?: number } | null;
  deliveryDate: string | null;
  paymentTermDays: number;
  deliveryAddress: string | null;
  termsConditions: string | null;
  subtotal: string;
  gstAmount: string;
  grandTotal: string;
  status: PoStatus;
  payStatus: PayStatus;
  /** How the line prices were determined: quote-backed vs manually entered. */
  priceSource?: 'Quoted' | 'Express' | 'Manual';
  items: PoItem[];
  grns?: { id: number; grnNo: string; grnDate: string; totalValue: string; stockStatus: string }[];
  grnCount?: number;
  pendingQty?: number;
  sla?: SlaBadge | null;
}

export interface PoFilter {
  status?: string;
  payStatus?: string;
  vendorId?: number;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface PoListResult {
  data: Po[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listPos(filter: PoFilter = {}): Promise<PoListResult> {
  const r = await api.get<PoListResult>('/po', { params: filter });
  return r.data;
}

/** Excel export of every record matching the filters + search (no page limit). */
export function poExportPath(filter: PoFilter): string {
  const { page: _p, pageSize: _s, ...rest } = filter;
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(rest).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])),
  ).toString();
  return `/po/export.xlsx${qs ? `?${qs}` : ''}`;
}

export async function getPo(id: number): Promise<Po> {
  const r = await api.get<Po>(`/po/${id}`);
  return r.data;
}

export async function createPo(payload: {
  poDate: string;
  vendorId: number;
  rfqId?: number;
  deliveryDate?: string;
  paymentTermDays?: number;
  deliveryAddress?: string;
  termsConditions?: string;
  subject?: string;
  items: Omit<PoItem, 'id' | 'total'>[];
}): Promise<Po> {
  const r = await api.post<Po>('/po', payload);
  return r.data;
}

export interface GeneratePoFromPrItem {
  itemId?: number;
  itemName: string;
  hsn?: string;
  qty: number;
  unit: string;
  unitPrice: number;
  gstPct?: number;
}

export interface GeneratePoFromPrPayload {
  poDate?: string;
  deliveryDate?: string;
  paymentTermDays?: number;
  deliveryAddress?: string;
  termsConditions?: string;
  /** Defaults to the PR's subject. */
  subject?: string;
  /** Edited lines. When omitted, the PR's own items are used unchanged. */
  items?: GeneratePoFromPrItem[];
}

/**
 * Fast-track: generate a Draft PO straight from an approved PR for one vendor.
 * Pass edited header fields + lines to mirror the New PO form.
 */
export async function generatePoFromPr(
  prId: number,
  vendorId: number,
  payload: GeneratePoFromPrPayload = {},
): Promise<Po> {
  const r = await api.post<Po>('/po/from-pr', { prId, vendorId, ...payload });
  return r.data;
}

export async function updatePo(id: number, payload: Partial<Po> & { items?: PoItem[] }): Promise<Po> {
  const r = await api.patch<Po>(`/po/${id}`, payload);
  return r.data;
}

export async function deletePo(id: number): Promise<void> {
  await api.delete(`/po/${id}`);
}

export async function sendPo(id: number): Promise<Po> {
  const r = await api.post<Po>(`/po/${id}/send`);
  return r.data;
}

export async function acknowledgePo(id: number): Promise<Po> {
  const r = await api.post<Po>(`/po/${id}/acknowledge`);
  return r.data;
}

export async function cancelPo(id: number, reason: string): Promise<Po> {
  const r = await api.post<Po>(`/po/${id}/cancel`, { reason });
  return r.data;
}

export async function markPoPaid(
  id: number,
  payload: { amount: number; mode: string; ref?: string },
): Promise<Po> {
  const r = await api.post<Po>(`/po/${id}/mark-paid`, payload);
  return r.data;
}
